import { useEffect, useState } from 'react';
import { UserDocumentationIcon } from '../../../../../components/buttons/IconButtons';
import TableFunction from './../../../../../components/table/TableFunction';
import {STATUS_MODEL} from '../../../../../enums/statusEnum';
import { SEARCH_MODEL } from '../../../../../components/table/Model/TableModel';
import { SimpleSearch } from '../../../../../reduxToolkit/Search/SearchApi';
import { useDispatch } from 'react-redux';
import {
    DocumentationUserUpdateStatusByUserIds,
    DocumentationVehicleUpdateStatusByVehicleIds,
    GetDocumentationRequestByUserTypeIdAndProviderId
} from '../../../../../reduxToolkit/DocumentationApproval/DocumentationApprovalApi';
import { errorMsg } from '../../../../../Apis/functions';
import { PROFILE_MODEL } from '../../../../DocumentationApprovalFlow/Model/Model';
import {toast} from 'react-toastify';
import {getToastMessageByStatus} from '../../../ApprovalTracker/Model/Model';

/**
 * @Author      Jhovanny Fragoso Marquez
 * @JobTitle    Frontend Developer
 * @Email       jfragoso@ibl.mx
 */

const RequestSubViewApp = ({
    searchElement,
    setIsAllChecked,
    option,
    userTypeId
}) => {
    // Access development mode from Redux state
    // Access development mode from Redux state
    const dispatch = useDispatch();

    // Local state variables
    const tableId = 'tbl_rsv_request'; // Table ID for DOM usage
    const searchId = 'txt_rsv_request'; // Search input ID for DOM usage
    const [results, setResults] = useState(''); // Store search results
    const [itemRequest, setItemRequest] = useState(''); // Store search results
    const [expandedRowId, setExpandedRowId] = useState(null); // Store ID of the currently expanded row
    const [showApprovedModal, setShowApprovedModal] = useState(false);
    const [showApproveButton, setShowApproveButton] = useState(false);
    const [selectedList, setSelectedList] = useState([]);
    const [requestList, setRequestList] = useState([]);
    const [modified, setModified] = useState(false);

    // Define the table columns
    const columns = [
        {
            field: 'companyName',
            label: 'name',
            classTableCell: 'w-100',
            className: 'w-100'
        },
        {
            field: 'totalRequest',
            label: 'number',
            render: (value) => {
                return (
                    <div className='d-flex justify-content-start align-items-center gap-2 font-color-shades-600'>
                        <UserDocumentationIcon className='icon-color-shades-600' />
                        {value} {t('requests').toLowerCase()}
                    </div>
                );
            },
        },
    ];

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page) => {
        const body = SEARCH_MODEL.setObject({ page: page, size: rowsPerPage, model: option, key: searchElement });
        dispatch(SimpleSearch({ body })).then(({ payload: { data: { data, success } } }) => {
            if (success === true) {
                setResults(data);
            }
        });
    }

    // Use TableFunction hook to handle table logic (checkbox, pagination, etc.)
    const {
        t,
        handleCheckboxChange,
        handleDeleteAll,
        pagination,
        page,
        rowsPerPage,
    } = TableFunction({
        selectedList,
        setSelectedList,
        data: results,
        labelRowsPerPage: 'requests_per_page',
        hasContainer: true,
        fixedPagination: false,
        setIsAllChecked,
        searchElement, //New
        getData,//New
        modified,//New
        setModified,//New
    });

    const rows = results?.content?.map(item => ({
        ...item,
        id: item.userId
    })) ?? [];

    // Enable approve button only when items are selected
    useEffect(() => {
        if (selectedList?.length > 0) {
            setShowApproveButton(true);
        } else {
            setShowApproveButton(false);
        }
    }, [selectedList]);


    useEffect(() => {
        if (modified) {
            apiDocumentationRequest(expandedRowId)
            setModified(false);
        }
    }, [modified]);

    /**
     * Function to handle row expansion
     */
    const handleRowExpand = (row) => {
        if (row?.contractorId) {
            apiDocumentationRequest(row.contractorId);
            setExpandedRowId(row.contractorId);
        }
        if (row?.supplierId) {
            apiDocumentationRequest(row.supplierId);
            setExpandedRowId(row.supplierId);
        }
    };

    // Handles check/uncheck on nested row items
    const handleChildCheckbox = (id, checked) => {
        setSelectedList((prev) => {
            if (checked) {
                return prev.includes(id) ? prev : [...prev, id];
            } else {
                return prev.filter((x) => x !== id);
            }
        });
    };

    /**
     * Fetches a contractor request by contractor ID and sets it in state.
     * Adds a `companyRequest` array containing the data itself for convenience.
     * Shows an error message if the operation fails.
     *
     * @param {string|number} id - Contractor ID, Supplier ID
     */
    const apiDocumentationRequest = (id) => {
        dispatch(GetDocumentationRequestByUserTypeIdAndProviderId({id, userTypeId: userTypeId }))
            .then(({ payload: { data: { data, success, code } } }) => {
            if (success) {
                let aux = [data];
                const buildData = { ...data, companyRequest: aux };

                setItemRequest(buildData);

                const items = [
                    ...(buildData.companyRequest ?? []),
                    ...(buildData.employeeRequest ?? []),
                    ...(buildData.vehicleRequest ?? [])
                ];

                setRequestList(prev => {
                    if (!prev?.length) return items;

                    const map = new Map(prev.map(item => [item.id, item]));

                    for (const item of items) {
                        map.set(item.id, item);
                    }

                    return Array.from(map.values());
                });

            } else errorMsg(code)
        })
    }

    /**
     * Handles approval for all selected items (users and/or vehicles) with an optional reason.
     * Updates the state, shows a success toast, or shows an error message if the operation fails.
     *
     * @param {string} reason - Optional comment or reason for approval
     */
    const handleApprovalAll = (reason) => {
        const result = buildRequest(reason);
        let vehicle = result.vehicle;
        let user = result.user;
        let promise;
        if (vehicle && user && !vehicle?.isEmpty && !user?.isEmpty) {
            promise = apiUpdateBoth(user, vehicle);
        } else if (user && !user?.isEmpty) {
            promise = dispatch(DocumentationUserUpdateStatusByUserIds(user));
        } else if (vehicle && !vehicle?.isEmpty) {
            promise = dispatch(DocumentationVehicleUpdateStatusByVehicleIds(vehicle));
        } else {
            promise = Promise.resolve({ payload: { data: { data: null, success: false } } });
        }
        promise.then(({payload: {data: { success, code }}}) => {
            if(success){
                setSelectedList([]);
                setModified(true);
                toast.success(t(getToastMessageByStatus(STATUS_MODEL.APPROVED)));
            }else errorMsg(code)
        });
    };


    /**
     * Updates both user and vehicle documentation statuses.
     * Executes sequential dispatches to ensure both updates are completed.
     *
     * @param {Object} user - User update data (userIds, statusId, reason, etc.)
     * @param {Object} vehicle - Vehicle update data (vehicleIds, statusId, reason, etc.)
     * @returns {Promise} - Resolves when both updates are completed
     */
    const apiUpdateBoth = async (user, vehicle) => {
        await dispatch(DocumentationUserUpdateStatusByUserIds(user));
        return dispatch(DocumentationVehicleUpdateStatusByVehicleIds(vehicle));
    }

    /**
     * Builds the request payload for updating documentation status of users and vehicles.
     * Filters the selected items by their level profile and creates separate objects for vehicles and users.
     * @param {string} reason - Optional reason/comment for the approval
     * @returns {Object} - Object containing `vehicle` and `user` payloads for update
     */
    const buildRequest = (  reason) => {
        const matches = requestList.filter(item => selectedList.includes(item.id));

        const vehicleIds = matches
            .filter(item => item?.levelProfile === PROFILE_MODEL.VEHICLE)
            .map(item => item.id);

        const userIds = matches
            .filter(item => item?.levelProfile === PROFILE_MODEL.COMPANY || item.levelProfile === PROFILE_MODEL.EMPLOYEE)
            .map(item => item.id);

        return {
            vehicle: { statusId: STATUS_MODEL.APPROVED, vehicleIds, reason: reason, isEmpty: vehicleIds.length === 0, userTypeId: userTypeId },
            user: { statusId: STATUS_MODEL.APPROVED, userIds, reason: reason, isEmpty: userIds.length === 0, userTypeId: userTypeId }
        };
    };


    return {
        t,
        tableId,
        searchId,
        handleCheckboxChange,
        handleDeleteAll,
        rowsPerPage,
        columns,
        handleRowExpand,
        results,
        setResults,
        showApprovedModal,
        setShowApprovedModal,
        selectedList,
        showApproveButton,
        handleChildCheckbox,
        pagination,
        page,
        itemRequest,
        handleApprovalAll,
        rows
    };
};

export default RequestSubViewApp; // Export the component for use in other parts of the app
